/**
 * WizardDateScreen Component (Web Version)
 * Maintains identical business logic from Mobile-client-core SchedulingWizardDateScreen.tsx
 * Features: Calendar with availability dots, services summary, auto-advance
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Calendar from 'react-calendar';
import { Loader2 } from 'lucide-react';
import { useWizardStore } from '../../stores/wizardStore';
import { getAvailableDatesForCalendar } from '../../services/wizardApiService';
import 'react-calendar/dist/Calendar.css';

const WizardDateScreen = () => {
  const navigate = useNavigate();
  
  const {
    selectedServices,
    selectedDate,
    availableDates,
    currentMonth,
    currentStep,
    isLoading,
    error,
    setSelectedDate,
    setAvailableDates,
    setCurrentMonth,
    setLoading,
    setError,
    clearError,
    canProceedToStep,
    goToNextStep,
    getTotalEstimatedTime
  } = useWizardStore();

  const [calendarValue, setCalendarValue] = useState(
    selectedDate ? new Date(selectedDate) : new Date()
  );

  // Format duration (identical to mobile)
  const formatDuration = (minutes) => {
    if (minutes === undefined || minutes === null || minutes <= 0) return '-';
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${h > 0 ? h + 'h ' : ''}${m > 0 ? m + 'min' : ''}`.trim() || '-';
  };

  // Load available dates for calendar month
  const loadAvailableDates = useCallback(async (year, month) => {
    if (selectedServices.length === 0) return;

    setLoading(true);
    clearError();

    try {
      const serviceIds = selectedServices.map(service => service.id);
      const dates = await getAvailableDatesForCalendar(serviceIds, year, month);
      setAvailableDates(dates);
      setCurrentMonth({ year, month });
    } catch (err) {
      setError('Erro ao carregar datas disponíveis. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [selectedServices, setLoading, clearError, setAvailableDates, setCurrentMonth, setError]);

  // Load dates when component mounts or month changes
  useEffect(() => {
    const now = new Date();
    loadAvailableDates(now.getFullYear(), now.getMonth() + 1);
  }, [loadAvailableDates]);

  // Handle calendar month change
  const handleActiveStartDateChange = ({ activeStartDate }) => {
    const year = activeStartDate.getFullYear();
    const month = activeStartDate.getMonth() + 1;
    
    if (year !== currentMonth.year || month !== currentMonth.month) {
      loadAvailableDates(year, month);
    }
  };

  // Handle date selection
  const handleDateSelect = useCallback((date) => {
    const dateString = date.toISOString().split('T')[0];

    if (!availableDates.includes(dateString)) {
      // Show unavailability feedback
      alert('Não há horários disponíveis para os serviços escolhidos nesta data.');
      return;
    }

    setSelectedDate(dateString);
    setCalendarValue(date);

    // Auto-advance to next step after 500ms delay (identical to mobile)
    setTimeout(() => {
      const nextStep = currentStep + 1;
      
      if (canProceedToStep(nextStep)) {
        goToNextStep();
        // TODO: Navigate to next step when implemented
        // navigate('/scheduling-wizard?step=2');
      } else {
        // Cannot proceed to next step
      }
    }, 500);
  }, [availableDates, setSelectedDate, canProceedToStep, goToNextStep, currentStep]);

  // Calendar tile content - add dots for availability (Luxury Design)
  const tileContent = ({ date, view }) => {
    if (view !== 'month') return null;

    const dateString = date.toISOString().split('T')[0];
    const today = new Date().toISOString().split('T')[0];

    // Don't show dots for past dates
    if (dateString < today) return null;

    const isAvailable = availableDates.includes(dateString);

    return (
      <div className="flex justify-center mt-1">
        <div
          className={`w-1 h-1 ${
            isAvailable ? 'bg-brand-dark dark:bg-brand-light' : 'bg-brand-dark/20 dark:bg-brand-light/20'
          }`}
        />
      </div>
    );
  };

  // Calendar tile class name - highlight selected date
  const tileClassName = ({ date, view }) => {
    if (view !== 'month') return '';
    
    const dateString = date.toISOString().split('T')[0];
    const today = new Date().toISOString().split('T')[0];
    
    // Past dates
    if (dateString < today) {
      return 'past-date';
    }
    
    // Selected date
    if (dateString === selectedDate) {
      return 'selected-date';
    }
    
    // Available dates
    if (availableDates.includes(dateString)) {
      return 'available-date';
    }
    
    // Unavailable dates
    return 'unavailable-date';
  };

  // Render services summary (just the chips) - Luxury Design
  const renderServicesSummary = () => (
    <div className="bg-brand-dark/5 dark:bg-brand-light/5 border border-brand-dark/10 dark:border-brand-light/10 p-4 mb-4">
      {/* Horizontal scrolling chips */}
      <div className="flex overflow-x-auto gap-2 scrollbar-hide">
        {selectedServices.map((service, index) => (
          <div
            key={service.cartItemId || service.id || index}
            className="flex-shrink-0 bg-brand-light dark:bg-brand-dark border border-brand-dark/20 dark:border-brand-light/20 px-4 py-3 min-w-24"
          >
            <div className="text-center">
              <div className="text-xs font-medium text-brand-dark dark:text-brand-light mb-1 tracking-wide">
                {service.name}
              </div>
              {service.selectedVariation?.name && service.selectedVariation.name !== 'Padrão' && (
                <div className="text-[10px] text-brand-dark/70 dark:text-brand-light/70 mb-1 tracking-wide">
                  {service.selectedVariation.name}
                </div>
              )}
              <div className="text-[10px] text-brand-dark/50 dark:text-brand-light/50 uppercase tracking-wider">
                {formatDuration(service.duration_minutes)}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Render loading state - Luxury Design
  const renderLoadingState = () => (
    <div className="flex flex-col items-center justify-center py-16">
      <div className="w-8 h-8 border-2 border-brand-dark dark:border-brand-light border-t-transparent mx-auto mb-4 animate-spin rounded-full"></div>
      <p className="text-brand-dark/60 dark:text-brand-light/60 text-xs uppercase tracking-wider">Carregando...</p>
    </div>
  );

  // Render error state - Luxury Design
  const renderErrorState = () => (
    <div className="text-center py-16">
      <p className="text-red-600 dark:text-red-400 mb-6 tracking-wide">{error}</p>
      <button
        onClick={() => {
          const now = new Date();
          loadAvailableDates(now.getFullYear(), now.getMonth() + 1);
        }}
        className="px-6 py-3 bg-brand-dark dark:bg-brand-light text-brand-light dark:text-brand-dark border border-brand-dark dark:border-brand-light font-medium uppercase tracking-widest text-sm hover:bg-brand-dark/90 dark:hover:bg-brand-light/90 transition-all duration-300"
      >
        Tentar novamente
      </button>
    </div>
  );

  return (
    <div className="h-full overflow-y-auto mobile-scroll">
      <div className="p-4 pb-8">
        {/* Services Summary */}
        {renderServicesSummary()}

        {/* Calendar Section - Luxury Design */}
        <div className="bg-brand-light dark:bg-brand-dark border border-brand-dark/10 dark:border-brand-light/10 p-4">
          {isLoading ? (
            renderLoadingState()
          ) : error ? (
            renderErrorState()
          ) : (
            <div className="calendar-container">
              <Calendar
                onChange={handleDateSelect}
                value={calendarValue}
                onActiveStartDateChange={handleActiveStartDateChange}
                tileContent={tileContent}
                tileClassName={tileClassName}
                minDate={new Date()}
                maxDate={new Date(Date.now() + 90 * 24 * 60 * 60 * 1000)} // 90 days ahead
                locale="pt-BR"
                next2Label={null}
                prev2Label={null}
                showNeighboringMonth={false}
                className="wizard-calendar"
              />
            </div>
          )}
        </div>

        {/* Selected Date Display - Luxury Design */}
        {selectedDate && (
          <div className="mt-4 p-4 border border-green-600/20 dark:border-green-400/20 bg-transparent">
            <p className="text-green-600 dark:text-green-400 font-medium tracking-wide capitalize">
              {new Date(selectedDate + 'T00:00:00').toLocaleDateString('pt-BR', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </p>
          </div>
        )}
      </div>

      {/* Calendar Custom Styles - Luxury Design */}
      <style>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }

        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }

        .calendar-container .wizard-calendar {
          border: none;
          font-family: inherit;
          width: 100%;
          background: transparent !important;
        }

        .calendar-container .react-calendar {
          background: transparent !important;
        }

        .calendar-container .react-calendar__navigation {
          margin-bottom: 1rem;
          display: flex;
          align-items: center;
          background: transparent !important;
        }

        .calendar-container .react-calendar__navigation button {
          color: #111111;
          font-weight: 600;
          font-size: 0.875rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          min-width: 44px;
          background: transparent !important;
          border: 1px solid rgba(17, 17, 17, 0.1);
        }

        .dark .calendar-container .react-calendar__navigation button {
          color: #F5F5F5;
          border-color: rgba(245, 245, 245, 0.2);
          background: transparent !important;
        }

        .calendar-container .react-calendar__navigation button:hover {
          background-color: rgba(17, 17, 17, 0.05) !important;
        }

        .dark .calendar-container .react-calendar__navigation button:hover {
          background-color: rgba(245, 245, 245, 0.1) !important;
        }

        .calendar-container .react-calendar__navigation button:disabled {
          background-color: transparent !important;
        }

        .calendar-container .react-calendar__month-view__weekdays {
          text-transform: uppercase;
          font-size: 0.625rem;
          letter-spacing: 0.1em;
          font-weight: 500;
          color: rgba(17, 17, 17, 0.5);
        }

        .dark .calendar-container .react-calendar__month-view__weekdays {
          color: rgba(245, 245, 245, 0.5);
        }

        .calendar-container .react-calendar__month-view__weekdays__weekday abbr {
          text-decoration: none;
        }

        .calendar-container .react-calendar__tile {
          background: transparent !important;
          border: 1px solid rgba(17, 17, 17, 0.1);
          font-weight: 500;
          aspect-ratio: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          color: #111111;
        }

        .dark .calendar-container .react-calendar__tile {
          border-color: rgba(245, 245, 245, 0.15);
          color: #F5F5F5;
          background: transparent !important;
        }

        .calendar-container .react-calendar__tile:enabled:hover {
          background-color: rgba(17, 17, 17, 0.05) !important;
        }

        .dark .calendar-container .react-calendar__tile:enabled:hover {
          background-color: rgba(245, 245, 245, 0.1) !important;
        }

        .calendar-container .react-calendar__tile.past-date {
          color: rgba(17, 17, 17, 0.3) !important;
          background-color: transparent !important;
        }

        .dark .calendar-container .react-calendar__tile.past-date {
          color: rgba(245, 245, 245, 0.3) !important;
          background-color: transparent !important;
        }

        .calendar-container .react-calendar__tile.selected-date,
        .calendar-container .react-calendar__tile--active,
        .calendar-container .react-calendar__tile--active:enabled:hover,
        .calendar-container .react-calendar__tile--active:enabled:focus,
        .calendar-container button.react-calendar__tile--active {
          background-color: #111111 !important;
          color: #F5F5F5 !important;
          border-color: #111111 !important;
        }

        .calendar-container .react-calendar__tile.selected-date *,
        .calendar-container .react-calendar__tile--active *,
        .calendar-container button.react-calendar__tile--active * {
          color: #F5F5F5 !important;
        }

        .dark .calendar-container .react-calendar__tile.selected-date,
        .dark .calendar-container .react-calendar__tile--active,
        .dark .calendar-container .react-calendar__tile--active:enabled:hover,
        .dark .calendar-container .react-calendar__tile--active:enabled:focus,
        .dark .calendar-container button.react-calendar__tile--active {
          background-color: #F5F5F5 !important;
          color: #111111 !important;
          border-color: #F5F5F5 !important;
        }

        .dark .calendar-container .react-calendar__tile.selected-date *,
        .dark .calendar-container .react-calendar__tile--active *,
        .dark .calendar-container button.react-calendar__tile--active * {
          color: #111111 !important;
        }

        .calendar-container .react-calendar__tile.available-date:hover {
          background-color: rgba(17, 17, 17, 0.05) !important;
        }

        .dark .calendar-container .react-calendar__tile.available-date:hover {
          background-color: rgba(245, 245, 245, 0.1) !important;
        }

        .calendar-container .react-calendar__tile.unavailable-date {
          color: rgba(17, 17, 17, 0.4);
        }

        .dark .calendar-container .react-calendar__tile.unavailable-date {
          color: rgba(245, 245, 245, 0.4);
        }

        .calendar-container .react-calendar__tile--now {
          background-color: rgba(17, 17, 17, 0.08) !important;
          border-color: rgba(17, 17, 17, 0.3) !important;
        }

        .dark .calendar-container .react-calendar__tile--now {
          background-color: rgba(245, 245, 245, 0.1) !important;
          border-color: rgba(245, 245, 245, 0.3) !important;
        }

        /* Override react-calendar default white backgrounds */
        .calendar-container .react-calendar__month-view__days__day--weekend {
          color: inherit;
        }

        .dark .calendar-container .react-calendar__month-view__days__day--weekend {
          color: #F5F5F5;
        }

        .calendar-container .react-calendar__month-view__days__day--neighboringMonth {
          color: rgba(17, 17, 17, 0.3);
        }

        .dark .calendar-container .react-calendar__month-view__days__day--neighboringMonth {
          color: rgba(245, 245, 245, 0.3);
        }
      `}</style>
    </div>
  );
};

export default WizardDateScreen;